import React, { useState, useRef, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import { Input } from './ui/input';
import { normalizeGreek } from '../utils/greekUtils';

const MachineSearchBox = ({ machines, customers, onSelect, placeholder = "Αναζήτηση με S/N, επωνυμία ή ΑΦΜ..." }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (value) => {
    setQuery(value);
    if (!value || value.length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    const normalized = normalizeGreek(value.toLowerCase());

    // Build customer AFM lookup
    const custMap = {};
    (customers || []).forEach(c => {
      custMap[c.id] = c;
    });

    const matched = (machines || []).filter(m => {
      const serial = normalizeGreek((m.machine_serial || '').toLowerCase());
      const custName = normalizeGreek((m.company_name || '').toLowerCase());
      const brand = normalizeGreek((m.brand || '').toLowerCase());
      const model = normalizeGreek((m.model || '').toLowerCase());
      const cust = custMap[m.customer_id];
      const afm = (cust?.afm || '').toLowerCase();

      return serial.includes(normalized) ||
             custName.includes(normalized) ||
             afm.includes(normalized) ||
             brand.includes(normalized) ||
             model.includes(normalized);
    }).slice(0, 10);

    setResults(matched);
    setIsOpen(matched.length > 0);
  };

  const handleSelect = (machine) => {
    setQuery(`${machine.machine_serial} - ${machine.company_name}`);
    setIsOpen(false);
    setResults([]);
    onSelect(machine);
  };

  const handleClear = () => {
    setQuery('');
    setResults([]);
    setIsOpen(false);
  };

  return (
    <div ref={wrapperRef} className="relative" data-testid="machine-search-box">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          type="text"
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={() => { if (results.length > 0) setIsOpen(true); }}
          placeholder={placeholder}
          className="pl-10 pr-8"
          data-testid="machine-search-input"
        />
        {query && (
          <button onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {isOpen && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-64 overflow-y-auto">
          {results.map((m) => (
            <button
              key={m.id}
              onClick={() => handleSelect(m)}
              className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-gray-100 last:border-0 transition-colors"
              data-testid={`search-result-${m.id}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-medium text-sm">{m.company_name}</span>
                  <span className="text-xs text-gray-500 ml-2">{m.brand} {m.model}</span>
                </div>
                <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded">{m.machine_serial}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MachineSearchBox;
